import React, { useRef, useState } from 'react';
import Hide from '@uber/base-icons/hide';
import Lock from '@uber/base-icons/lock';
import { useStyletron } from 'baseui';
import { useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { STORE_TYPE } from '../../../common/types/types';
import type { ModernizedGenericAnnotation } from '..';
import { drawerAtom } from '../atoms/drawer.atom';
import { EmptyState } from '../common/empty-state';
import { SIDE_PANEL_ACTIONS } from '../const';
import { useDispatch } from '../hooks/use-dispatch';
import { usePendingAnnotations } from '../hooks/use-pending-annotations';
import { type PendingAnnotationDetailsProps, PendingAnnotationDetails } from './pending-annotation-details';
import { PendingEditActionModal } from './pending-edit-action-modal';
import { usePendingAnnotationDrawer } from './use-pending-annotation-drawer';
const getAnnotationDetails = (annotation: ModernizedGenericAnnotation) => {
  const canAddEndEnhancer = annotation.hidden || annotation.locked;
  return {
    label: 'Annotation',
    value: `${annotation.annotationLabel.title} [${annotation.id}]`,
    endEnhancer: canAddEndEnhancer ? <>
        {annotation.hidden && <Hide />}
        {annotation.locked && <Lock />}
      </> : undefined
  };
};
const getAttributesDetails = (annotation: ModernizedGenericAnnotation) => {
  if (!annotation.tags?.length) {
    return;
  }
  return {
    label: 'Classification',
    value: annotation.tags?.map(tag => tag.label).join(', ') ?? ''
  };
};
const MODAL_TITLE = 'Enable object editing';
const MODAL_DESCRIPTION = 'This object is either hidden or locked. To complete questionnaire, it must be unhidden or unlocked. This action will change all the occurrences of this annotation.';
export const PendingAnnotationPanel = () => {
  const pendingAnnotations = usePendingAnnotations();
  const [css, theme] = useStyletron();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const setDrawer = useSetAtom(drawerAtom);
  const dispatch = useDispatch();
  const currentFocusedAnnotationId = useRef<string | null>(null);
  const {
    openPendingItemDrawer
  } = usePendingAnnotationDrawer();
  const getList = (annotation: ModernizedGenericAnnotation) => {
    const list: Array<PendingAnnotationDetailsProps['list'][number]> = [];
    list.push(getAnnotationDetails(annotation));
    const attributesDetails = getAttributesDetails(annotation);
    if (attributesDetails) {
      list.push(attributesDetails);
    }
    return list;
  };
  const showEditAnnotationDrawer = (id: string) => {
    setDrawer(RESET);
    dispatch({
      type: SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_START,
      annotationId: id
    });
    // Use requestAnimationFrame to synchronize drawer transitions with browser rendering cycle.
    // Waits for the current drawer's close animation/DOM updates to complete before opening new drawer.
    // Prevents visual glitches and animation conflicts during drawer state transitions.
    // Required here because we're transitioning from edit annotation drawer back to pending panel.
    requestAnimationFrame(() => {
      setDrawer(prev => ({
        ...prev,
        onClose: () => {
          prev.onClose?.();
          // Second requestAnimationFrame ensures the drawer close completes before reopening pending panel
          requestAnimationFrame(() => {
            openPendingItemDrawer();
          });
        }
      }));
    });
  };
  const onCompleteQuestionnaire = (id: string) => {
    const isNotEditable = pendingAnnotations.some(annotation => annotation.id === id && (annotation.hidden || annotation.locked));
    if (isNotEditable) {
      currentFocusedAnnotationId.current = id;
      setIsModalOpen(true);
    } else {
      showEditAnnotationDrawer(id);
    }
  };
  const onEnableEditing = () => {
    const id = currentFocusedAnnotationId.current;
    if (!id) {
      return;
    }
    dispatch({
      type: SIDE_PANEL_ACTIONS.SHOW_HIDE_ANNOTATIONS,
      annotationId: id,
      hidden: false,
      storeType: STORE_TYPE.ANNOTATION_STORE
    });
    dispatch({
      type: SIDE_PANEL_ACTIONS.LOCK_ANNOTATIONS,
      annotationId: id,
      locked: false
    });
    setIsModalOpen(false);
    showEditAnnotationDrawer(id);
    currentFocusedAnnotationId.current = null;
  };
  return <>
      <div className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.sizing.scale600,
      overflowY: 'auto',
      height: '100%'
    })}>
        {pendingAnnotations.map(annotation => <PendingAnnotationDetails key={annotation.id} id={annotation.id} list={getList(annotation)} onAction={onCompleteQuestionnaire} />)}
        {pendingAnnotations.length === 0 && <EmptyState size={48} title="No pending annotations" message="All annotations have been completed" />}
      </div>
      <PendingEditActionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onEnableEditing={onEnableEditing} title={MODAL_TITLE} description={MODAL_DESCRIPTION} />
    </>;
};